import type {Metadata} from "@khanacademy/wonder-stuff-core";
import type {SentryData} from "@khanacademy/wonder-stuff-sentry";

import type {KAError} from "./ka-error.ts";

// sync-start:error-kinds 1540518554 https://github.com/Khan/webapp/blob/master/pkg/lib/errors/kinds.go
/**
 * @typedef {Object} Errors The different errors in our error taxononomy.
 *
 * @property {ErrorKind} NotFound A resource was not found. If the resource
 * couldn't be retrieved due to access control use {@link Unauthorized}
 * instead. If the resource couldn't be found because the input was invalid use
 * {@link InvalidInput} instead.
 *
 * @property {ErrorKind} InvalidInput There was a problem with provided input,
 * such as the wrong format. This indicates inputs that are problematic
 * regardless of the state of the system. If the input was valid but caused
 * problems anyway consider {@link NotAllowed}.
 *
 * @property {ErrorKind} NotAllowed There was a problem due to the state of the
 * system not matching the requested operation or input. For example, trying to
 * create a username that is valid, but is already taken by another user. Use
 * {@link InvalidInput} instead when the input isn't valid regardless of
 * the state of the system. Use {@link NotFound} when the failure is due
 * to not being able to find a resource.
 *
 * @property {ErrorKind} Unauthorized There was an access control problem
 *
 * @property {ErrorKind} Internal The function failed for a reason unrelated
 * to invalid input or problems working with a remote system. This is the most
 * commonly used error kind.
 *
 * @property {ErrorKind} NotImplemented The function isn't implemented
 *
 * @property {ErrorKind} GraphqlResponse The given graphql request
 * successfully ran, but returned a struct with an "error" field set.
 *
 * @property {ErrorKind} TransientKhanService There was a problem when
 * contacting another Khan service that might be resolvable by retrying.
 *
 * @property {ErrorKind} KhanService There was a non-transient problem when
 * contacting another Khan service.
 *
 * @property {ErrorKind} TransientService There was a problem when making a
 * request to a non-Khan service, e.g. datastore that might be resolvable by
 * retrying.
 *
 * @property {ErrorKind} Service There was a non-transient problem when making
 * a request to a non-Khan service, e.g. datastore.
 */

/**
 * @type {Errors} A utility for referencing our error taxonomy.
 */
export const Errors = Object.freeze({
    NotFound: "NotFound",
    InvalidInput: "InvalidInput",
    NotAllowed: "NotAllowed",
    Unauthorized: "Unauthorized",
    Internal: "Internal",
    NotImplemented: "NotImplemented",
    GraphqlResponse: "GraphqlResponse",
    TransientKhanService: "TransientKhanService",
    KhanService: "KhanService",
    TransientService: "TransientService",
    Service: "Service",
});
// sync-end:error-kinds

/**
 * What kind of error is being reported.
 *
 * This type represents our error taxonomy:
 *  - https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/150208513/Goliath+Errors+Best+Practices
 */
export type ErrorKind = (typeof Errors)[keyof typeof Errors];

/**
 * Different logging features that can be enabled.
 */
export type Features = Readonly<{
    /**
     * Indicates if logging is enabled.
     *
     * Logging is enabled for the development build, when logged in with
     * dev/admin type permissions, when serverside, or when force enabled
     * by query param.
     */
    logging: boolean;
}>;

/**
 * Represents an error.
 */
export type AmbiguousError = string | Error;

export interface LogErrorOptions {
    cause?: Error | null | undefined;
    metadata?: Metadata | null | undefined;
    sentryData?: Partial<SentryData> | null | undefined;
}

/**
 * Provides logging utilities.
 */
export interface IAmbiguousErrorLog {
    /**
     * Log a message and optional metadata.
     *
     * @param {string} message The message to log.
     * @param {Metadata} [metadata] Metadata about the logged message.
     * @param {...Array<mixed>} [args] Additional data about the logged message.
     * This additional data will not be included in metadata logs.
     */
    log(
        message: string,
        metadata?: Metadata | null | undefined,
        ...args: Array<unknown>
    ): void;
    /**
     * Log an error and optional metadata.
     *
     * @param {AmbiguousError} error The error to be logged.
     * @param {ErrorKind} kind The error kind for this error from our
     * error taxonomy, {@link Errors}
     * @param {LogErrorOptions} [options] Options such as a causal error,
     * loggable metadata, or data to be added to sentry.
     */
    error(
        error: AmbiguousError,
        kind: ErrorKind,
        options?: LogErrorOptions,
    ): void;
    /**
     * Log an error.
     *
     * @param {KAError} error The error to be logged.
     */
    error(error: KAError): void;
}

/**
 * Provides logging utilities.
 */
export interface IKAErrorLog {
    /**
     * Log a message and optional metadata.
     *
     * @param {string} message The message to log.
     * @param {Metadata} [metadata] Metadata about the logged message.
     */
    log(
        message: string,
        metadata?: Metadata | null | undefined,
        ...args: Array<unknown>
    ): void;
    /**
     * Log an error and optional metadata.
     *
     * @param {KAError} error The error to be logged.
     */
    error(error: KAError): void;
}

export interface ErrorWithResponse extends KAError {
    response: Response;
}
